import { logger } from 'Common/core';
import Api from './Api';

function required(value) {
    if (!value) return 'Form.Error.Required';
}

function noop() {
    return undefined;
}

export default class ExtForm {
    /**
     * Show or hide field-level error messages.
     *
     * @param {string} inputSelector  — CSS selector for the <input>
     * @param {string} errorSelector  — CSS selector for the container with <span data-error="...">
     * @param {string|string[]} messages
     */
    static showErrors(inputSelector, errorSelector, messages) {
        const msgs = (Array.isArray(messages) ? messages : [messages]).filter(Boolean);
        const inputEl = document.querySelector(inputSelector);
        const errorEl = document.querySelector(errorSelector);

        if (!inputEl || !errorEl) return;

        // hide all currently visible spans
        errorEl.querySelectorAll('span.d-block').forEach((span) => {
            span.classList.remove('d-block');
            span.classList.add('d-none');
        });

        if (msgs.length) {
            inputEl.classList.add('invalid');
            errorEl.classList.remove('d-none');
            msgs.forEach((msg) => {
                const span = errorEl.querySelector(`span[data-error="${msg}"]`);

                if (span) {
                    span.classList.remove('d-none');
                    span.classList.add('d-block');
                }
            });
        } else {
            inputEl.classList.remove('invalid');
            errorEl.classList.add('d-none');
        }
    }

    /**
     * Builds a function that validates every field, toggles errors,
     * and enables/disables the submit button.
     */
    static createValidator(state, validators, btn, { fieldErrorClass, buttonAlwaysEnabled }) {
        return () => {
            let formIsValid = true;

            Object.keys(state).forEach((fieldId) => {
                const inputEl = document.getElementById(fieldId);

                if (!inputEl) return;

                const message = validators[fieldId](inputEl.value);

                // only show errors once the field has been "touched"
                if (state[fieldId].touched) {
                    ExtForm.showErrors(`#${fieldId}`, `#${fieldId} + .${fieldErrorClass}`, message);
                }

                state[fieldId].valid = !message?.length;
                if (!state[fieldId].valid) formIsValid = false;
            });

            if (!buttonAlwaysEnabled) {
                btn.disabled = !formIsValid;
            }

            return formIsValid;
        };
    }

    /**
     * wires up the form: validation, submit handler, API calls, etc.
     */
    static initForm(
        uid,
        allFields,
        apiEndpoint,
        {
            fieldErrorClass = 'invalid-feedback',
            apiErrorSelector = '#api-errormessage',
            onComplete,
            onError,
            buttonAlwaysEnabled = false,
            scrollToTop = true,
        } = {},
        clearFields = true
    ) {
        const form = document.getElementById(uid);
        const btn = form?.querySelector('[type="submit"]');
        const apiError = document.querySelector(apiErrorSelector);

        if (!form || !btn || !apiError) {
            logger.error(`ExtForm.initForm: missing elements for form #${uid}`);
            return () => false;
        }

        const fields = allFields.filter((f) => !f.hidden).map((f) => f.id);
        // initialize touched/valid state
        const state = Object.fromEntries(fields.map((id) => [id, { touched: false, valid: false }]));

        // pick required or custom validator
        const validators = Object.fromEntries(
            fields.map((id) => {
                const inputEl = document.getElementById(id);
                const hasRequired = inputEl?.hasAttribute('required');
                const fieldDef = allFields.find((f) => f.id === id);
                const fn =
                    typeof fieldDef.validator === 'function' ? fieldDef.validator : hasRequired ? required : noop;

                return [id, fn];
            })
        );

        const validator = ExtForm.createValidator(state, validators, btn, { fieldErrorClass, buttonAlwaysEnabled });

        // helpers to disable/enable fields + button
        const disableForm = () => {
            fields.forEach((id) => {
                const el = document.getElementById(id);

                if (el) el.setAttribute('disabled', '');
            });
            btn.disabled = true;
        };

        const enableForm = () => {
            fields.forEach((id) => {
                const el = document.getElementById(id);

                if (el) el.removeAttribute('disabled');
            });
            btn.disabled = false;
        };

        // initial per‐field wiring
        function setupField(id, clearValue = true, validateOnInput = false) {
            const el = document.getElementById(id);

            if (!el) return;

            // reset state
            state[id].touched = false;
            state[id].valid = false;

            if (clearValue) el.value = '';

            // mark touched on first interaction
            const touchHandler = () => {
                state[id].touched = true;
                el.removeEventListener('input', touchHandler);
                el.removeEventListener('blur', touchHandler);
            };

            el.addEventListener('input', touchHandler);
            el.addEventListener('blur', touchHandler);

            // optionally hook up live validation
            if (validateOnInput) {
                el.addEventListener('input', validator);
                el.addEventListener('blur', validator);
            }
        }

        function initFields(clearValues = true, liveSetup = false) {
            fields.forEach((id) => setupField(id, clearValues, liveSetup));
            validator();
        }

        // clear any existing API errors
        function handleErrors(errors) {
            apiError.innerHTML = '';
            apiError.classList.remove('d-none', 'alert-success');
            apiError.classList.add('alert-danger');
            (errors || []).forEach((msg) => {
                const span = document.createElement('span');

                span.className = 'd-block';
                span.textContent = msg;
                apiError.append(span);
            });
            apiError.classList.remove('d-none');
        }

        // wire up form submit
        form.addEventListener('submit', (evt) => {
            evt.preventDefault();
            if (!validator()) return;

            // serialize like jQuery: URL-encoded string
            const payload = new URLSearchParams(new FormData(form)).toString();

            // clear old API errors
            apiError.classList.add('d-none');

            disableForm();
            const initForm = initFields;

            Api.POST(apiEndpoint, payload, {
                safe: false,
                noApi: true,
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            })
                .then(({ success, returnUrl, errors, message }) => {
                    if (success) {
                        if (scrollToTop) window.scrollTo(0, 0);
                        if (onComplete) {
                            onComplete({ success, message, enableForm, initForm, returnUrl });
                        } else if (returnUrl) {
                            window.location.replace(decodeURIComponent(returnUrl));
                        } else {
                            window.location.reload();
                        }
                    } else {
                        handleErrors(errors || (message ? [message] : []));
                        if (onError) onError();
                        enableForm();
                    }
                })
                .catch((e) => {
                    handleErrors(e?.data?.errors || [e?.message || e]);
                    if (onError) onError();
                    enableForm();
                });
        });

        // do the initial setup
        initFields(clearFields, true);

        // return so you can trigger validation programmatically if needed
        return validator;
    }
}
